import React, { FC, useCallback, useEffect, useState } from "react";
import { Image, Platform, StyleSheet, Text, TouchableOpacity, View } from "react-native";

/**
 * Top banner shown on Android Chrome / mobile browsers to nudge users into
 * the Play Store. Renders nothing on iOS (Safari already shows the
 * Smart App Banner from <meta name="apple-itunes-app">) or on native.
 *
 * Dismissal is sticky for 30 days via localStorage.
 */

const STORAGE_KEY = "onespace.nativeBanner.dismissedAt";
const DISMISS_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.theonespace.OneSpace";

function shouldShow(): boolean {
  if (Platform.OS !== "web") return false;
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;

  const ua = navigator.userAgent || "";
  const isAndroid = /android/i.test(ua);
  if (!isAndroid) return false;

  // Skip when the page is already running as an installed PWA — the user
  // has clearly chosen the web experience.
  const isStandalone =
    window.matchMedia?.("(display-mode: standalone)").matches === true ||
    (navigator as any).standalone === true;
  if (isStandalone) return false;

  try {
    const dismissedAt = Number(window.localStorage.getItem(STORAGE_KEY) ?? 0);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_WINDOW_MS) return false;
  } catch {
    // localStorage can throw in private mode; treat as not-dismissed.
  }

  return true;
}

const NativeAppBanner: FC = () => {
  const [visible, setVisible] = useState<boolean>(false);

  useEffect(() => {
    setVisible(shouldShow());
  }, []);

  const dismiss = useCallback(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      // Ignore — user just won't have their dismissal persisted.
    }
    setVisible(false);
  }, []);

  const openPlayStore = useCallback(() => {
    window.location.href = PLAY_STORE_URL;
  }, []);

  if (!visible) return null;

  return (
    <View style={styles.container} testID="native-app-banner">
      <TouchableOpacity
        accessibilityLabel="Dismiss"
        onPress={dismiss}
        style={styles.dismiss}
        testID="native-app-banner-dismiss"
      >
        <Text style={styles.dismissText}>×</Text>
      </TouchableOpacity>

      <Image
        source={{ uri: "/pwa/apple-touch-icon/apple-touch-icon-180.png" }}
        style={styles.icon}
        accessibilityLabel="OneSpace icon"
      />

      <View style={styles.copy}>
        <Text style={styles.title}>OneSpace</Text>
        <Text style={styles.subtitle}>Faster, smoother in the Android app</Text>
      </View>

      <TouchableOpacity
        accessibilityRole="button"
        onPress={openPlayStore}
        style={styles.cta}
        testID="native-app-banner-install"
      >
        <Text style={styles.ctaText}>Install</Text>
      </TouchableOpacity>
    </View>
  );
};

export default NativeAppBanner;

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: "#f2f2f7",
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: "#d1d1d6",
    gap: 10,
  },
  dismiss: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  dismissText: {
    fontSize: 20,
    color: "#6e6e73",
    lineHeight: 20,
  },
  icon: {
    width: 36,
    height: 36,
    borderRadius: 8,
  },
  copy: {
    flex: 1,
    flexDirection: "column",
  },
  title: {
    fontSize: 14,
    fontWeight: "600",
    color: "#1c1c1e",
  },
  subtitle: {
    fontSize: 12,
    color: "#6e6e73",
  },
  cta: {
    backgroundColor: "#4630eb",
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 100,
  },
  ctaText: {
    color: "#ffffff",
    fontWeight: "600",
    fontSize: 13,
  },
});
